import {ExperimentalInputEvaluatedValues, ExperimentalInputs} from 'procedures/planning/ProcedurePlanTypes';

import {ExperimentFlowchart, ExperimentMethod, prepareInitialExperimentalInputs} from './ExperimentalInputs';

/**
 * Computes a stable fingerprint for a method based on its parameter IDs.
 * These IDs (procedureDataParameterId, procedureMaterialInputId, procedureEquipmentId)
 * are stable across publishes, unlike the method's own id and sourceMethodId.
 */
function getMethodFingerprint(method: ExperimentMethod): string {
  const paramIds = method.dataParameterSettings.map((p) => p.version.procedureDataParameterId);
  const materialIds = method.materialInputSettings.map((m) => m.version.procedureMaterialInputId);
  const equipmentIds = method.equipmentSettings.map((e) => e.version.procedureEquipmentId);
  return JSON.stringify({paramIds, materialIds, equipmentIds});
}

/**
 * Creates a map from method fingerprint to ExperimentMethod.
 * The fingerprint is based on stable parameter IDs.
 */
function createMethodsByFingerprint(methods: Array<ExperimentMethod>): Map<string, ExperimentMethod> {
  return new Map(methods.map((method) => [getMethodFingerprint(method), method]));
}

/**
 * Checks if inputs can be preserved when updating to a new procedure version.
 * Inputs can only be preserved if:
 * 1. Both versions have the same number of methods
 * 2. Every old method has a matching new method with the same parameter IDs
 *
 * Methods are matched by their parameter IDs (procedureDataParameterId,
 * procedureMaterialInputId, procedureEquipmentId) which are stable across publishes.
 */
function canPreserveInputs(oldFlowchart: ExperimentFlowchart, newFlowchart: ExperimentFlowchart): boolean {
  if (oldFlowchart.nodes.length !== newFlowchart.nodes.length) {
    return false;
  }

  const oldMethodsByFingerprint = createMethodsByFingerprint(oldFlowchart.nodes);
  const newMethodsByFingerprint = createMethodsByFingerprint(newFlowchart.nodes);

  // Fingerprint collisions (e.g. multiple methods with no parameters) would cause
  // incorrect matching — bail out rather than silently mapping the wrong inputs.
  if (
    oldMethodsByFingerprint.size !== oldFlowchart.nodes.length ||
    newMethodsByFingerprint.size !== newFlowchart.nodes.length
  ) {
    return false;
  }

  return oldFlowchart.nodes.every((oldMethod) =>
    newMethodsByFingerprint.has(getMethodFingerprint(oldMethod))
  );
}

/**
 * Preserves user inputs from the current experimental inputs when updating to a new procedure version.
 *
 * Methods are matched across versions by their parameter IDs
 * (procedureDataParameterId, procedureMaterialInputId, procedureEquipmentId),
 * which are stable across publishes. The method-level id, nodeId, and sourceMethodId
 * all change on each publish and cannot be used for matching.
 *
 * If any structural difference is found, all inputs are cleared to start fresh.
 */
export function preserveProcedureInputsOnVersionUpdate(
  currentInputs: ExperimentalInputs,
  currentFlowchart: ExperimentFlowchart,
  newFlowchart: ExperimentFlowchart,
  numConditions: number
): ExperimentalInputs {
  const newInputs = prepareInitialExperimentalInputs(numConditions, newFlowchart.nodes);
  const oldMethodsByFingerprint = createMethodsByFingerprint(currentFlowchart.nodes);

  if (!canPreserveInputs(currentFlowchart, newFlowchart)) {
    return newInputs;
  }

  for (const newMethod of newFlowchart.nodes) {
    const oldMethod = oldMethodsByFingerprint.get(getMethodFingerprint(newMethod));
    if (oldMethod && currentInputs[oldMethod.id]) {
      newInputs[newMethod.id] = {
        ...newInputs[newMethod.id],
        values: currentInputs[oldMethod.id].values,
      };
    }
  }

  return newInputs;
}

/**
 * Builds evaluated values re-keyed to match the new procedure version's method IDs.
 *
 * When applying a procedure version update, `preserveProcedureInputsOnVersionUpdate` produces
 * ExperimentalInputs keyed by the new version's method IDs, but the existing evaluatedValues
 * are keyed by the old version's method IDs. This function maps old evaluated values to new
 * method IDs via parameter fingerprints, falling back to raw preserved input values for any
 * methods that don't have a corresponding entry in the old evaluated values.
 */
export function buildEvaluatedValuesForVersionUpdate(
  currentEvaluatedValues: ExperimentalInputEvaluatedValues,
  currentFlowchart: ExperimentFlowchart,
  newFlowchart: ExperimentFlowchart,
  preservedInputs: ExperimentalInputs
): ExperimentalInputEvaluatedValues {
  const oldMethodsByFingerprint = createMethodsByFingerprint(currentFlowchart.nodes);

  const newEvaluatedValues: ExperimentalInputEvaluatedValues = {};
  for (const newMethod of newFlowchart.nodes) {
    const oldMethod = oldMethodsByFingerprint.get(getMethodFingerprint(newMethod));
    const oldEval = oldMethod ? currentEvaluatedValues[oldMethod.id] : undefined;

    if (oldEval) {
      newEvaluatedValues[newMethod.id] = oldEval;
    } else {
      newEvaluatedValues[newMethod.id] = {
        blockingValueErrorCount: 0,
        values: preservedInputs[newMethod.id].values,
      };
    }
  }
  return newEvaluatedValues;
}

/**
 * Checks if user inputs would be preserved when updating to a new procedure version.
 * Inputs are only preserved if both versions have the same methods with matching
 * parameter IDs.
 */
export function getPreservationSummary(
  currentFlowchart: ExperimentFlowchart,
  newFlowchart: ExperimentFlowchart
): {willPreserveInputs: boolean} {
  const willPreserveInputs = canPreserveInputs(currentFlowchart, newFlowchart);
  return {willPreserveInputs};
}
